/**
 * Contas a Receber — regras puras, compartilhadas entre o servidor e as telas (sem dependências de Node). Lógica central
 * (24/10/2026): prever → comparar previsto com real → resgatar/emitir nota → receber → conciliar.
 *  - regra de recebimento do PARCEIRO: cadastro único (parametrização, regra-recebimento.ts), copiada na Proposta;
 *  - condição da OPERAÇÃO, campo a campo, com a ORIGEM de cada um (congelada na operação, complemento em lote do
 *    Financeiro, sugestão conferida, definição, ajuste);
 *  - distribuição proporcional às parcelas do Cliente (centavos; resíduo no último item; soma exata);
 *  - liberação a partir dos FATOS com data efetiva (pagamento do Cliente + reconhecimento do pagamento, marco + prazo)
 *    e repasse pelo marco e prazo acordados (dias corridos ou úteis);
 *  - PREVISÃO desde o faturamento do parceiro: vencimento previsto da parcela do Cliente → liberação prevista → data
 *    esperada de recebimento pela Inttrix, cada uma marcada como estimativa enquanto o fato não ocorre; sem dado
 *    suficiente, a previsão diz exatamente o que falta. Nada é inventado.
 * Receita no mês da nota da Inttrix, pelo valor da nota (decisão de 23/10/2026).
 */
import {
  A_CONFIRMAR,
  diasTexto,
  LIBERACOES_POR_PAGAMENTO,
  MARCOS_LIBERACAO,
  MARCOS_REPASSE,
  rotuloDe,
  TIPOS_LIBERACAO,
  type MarcoLiberacao,
  type MarcoRepasse,
  type Modalidade,
  type OrigemConfirmacao,
  type RegraRecebimento,
  type TipoDias,
  type TipoLiberacao,
} from './regra-recebimento.ts';

export {
  A_CONFIRMAR,
  liberacaoPelaComissao,
  LIBERACOES_POR_PAGAMENTO,
  MARCOS_LIBERACAO,
  MARCOS_REPASSE,
  normalizarRegraRecebimento,
  ORIGENS_CONFIRMACAO,
  regraRecebimentoVazia,
  regraRecebimentoVaziaDe,
  rotuloDe,
  TIPOS_LIBERACAO,
} from './regra-recebimento.ts';
export type { MarcoLiberacao, MarcoRepasse, Modalidade, OrigemConfirmacao, RegraRecebimento, TipoDias, TipoLiberacao } from './regra-recebimento.ts';

export const RECONHECIMENTO = 'No mês da nota da Inttrix';
export const AJUDA_RECONHECIMENTO = 'Decisão aprovada em 23/10: a receita pertence ao mês em que a Inttrix emite a sua própria nota, pelo valor da nota (o faturamento do parceiro não é faturamento da Inttrix). Descontos (IR, antecipação) ficam separados e o caixa segue a data efetiva do recebimento. Os dados ficam gravados para a DRE futura; a DRE ainda não está implementada.';

// ---------------------------------------------------------------- condição da operação

export const MODALIDADES: Record<Modalidade, string> = { venda: 'Venda', locacao_mensal: 'Locação mensal', locacao_projeto: 'Locação por projeto', trade_in: 'Trade-in' };
/** De onde veio cada campo da condição. Nulo = não definido na operação ("A confirmar"). */
export type OrigemCampo = 'operacao' | 'complemento' | 'sugestao_parceiro' | 'sugestao_comissao' | 'regra_parceiro' | 'definicao' | 'ajuste';
export const ROTULO_ORIGEM: Record<OrigemCampo, string> = {
  operacao: 'congelada na operação',
  complemento: 'complemento em lote do Financeiro (regra do parceiro, com autorização)',
  sugestao_parceiro: 'sugestão da regra atual do parceiro, conferida',
  sugestao_comissao: 'sugestão pela frequência da comissão, conferida',
  regra_parceiro: 'regra do parceiro vigente na criação',
  definicao: 'definida no Receber (estava a confirmar)',
  ajuste: 'ajuste no Receber, com justificativa',
};
export interface CampoLiberacao {
  tipo: TipoLiberacao | null;
  marco: MarcoLiberacao | null;
  dias: number | null;
  tipoDias: TipoDias | null;
  /** dias entre o pagamento do Cliente ao parceiro e o reconhecimento pelo parceiro (só liberações por pagamento) */
  reconhecimentoDias: number | null;
  reconhecimentoTipoDias: TipoDias | null;
  origem: OrigemCampo | null;
  detalhe: string;
}
export interface CampoRepasse {
  marco: MarcoRepasse | null;
  /** descrição do marco "Outro" (sem data projetável) */
  descricao: string;
  dias: number | null;
  tipoDias: TipoDias | null;
  origem: OrigemCampo | null;
  detalhe: string;
}
export interface CampoConfirmacao {
  valor: OrigemConfirmacao | null;
  origem: OrigemCampo | null;
  detalhe: string;
}
export interface CondicaoOperacao {
  liberacao: CampoLiberacao;
  repasse: CampoRepasse;
  confirmacao: CampoConfirmacao;
}
export type NomeCampo = keyof CondicaoOperacao;
export const ROTULO_CAMPO: Record<NomeCampo, string> = { liberacao: 'Liberação', repasse: 'Prazo de repasse', confirmacao: 'Confirmação do valor' };

export const liberacaoVazia = (): CampoLiberacao => ({ tipo: null, marco: null, dias: null, tipoDias: null, reconhecimentoDias: null, reconhecimentoTipoDias: null, origem: null, detalhe: 'não definida na operação' });
export const repasseVazio = (): CampoRepasse => ({ marco: null, descricao: '', dias: null, tipoDias: null, origem: null, detalhe: 'não definido na operação' });
export const confirmacaoVazia = (): CampoConfirmacao => ({ valor: null, origem: null, detalhe: 'não definida na operação' });

/** Completa campos gravados antes da parametrização (sem reconhecimento nem descrição do marco). */
export function normalizarCondicao(c: Partial<CondicaoOperacao> | null | undefined): CondicaoOperacao {
  return {
    liberacao: { ...liberacaoVazia(), ...(c?.liberacao ?? {}) },
    repasse: { ...repasseVazio(), ...(c?.repasse ?? {}) },
    confirmacao: { ...confirmacaoVazia(), ...(c?.confirmacao ?? {}) },
  };
}

/** Os três campos a partir de uma regra (do parceiro, congelada, complementada ou ajustada), com a origem informada. */
export function camposDaRegra(r: RegraRecebimento, origem: OrigemCampo, detalhe: string): CondicaoOperacao {
  const porPagamento = !r.liberacao || LIBERACOES_POR_PAGAMENTO.includes(r.liberacao);
  return {
    liberacao: r.liberacao
      ? { tipo: r.liberacao, marco: r.marcoLiberacao, dias: r.diasLiberacao, tipoDias: r.tipoDiasLiberacao, reconhecimentoDias: porPagamento ? r.diasReconhecimento : null, reconhecimentoTipoDias: porPagamento ? r.tipoDiasReconhecimento : null, origem, detalhe }
      : liberacaoVazia(),
    repasse: r.marcoRepasse || r.diasRepasse !== null ? { marco: r.marcoRepasse, descricao: r.marcoRepasseDescricao ?? '', dias: r.diasRepasse, tipoDias: r.tipoDiasRepasse, origem, detalhe } : repasseVazio(),
    confirmacao: r.confirmacao ? { valor: r.confirmacao, origem, detalhe } : confirmacaoVazia(),
  };
}

export const liberacaoCompleta = (l: Pick<CampoLiberacao, 'tipo' | 'marco' | 'dias' | 'tipoDias'>) =>
  Boolean(l.tipo) && (l.tipo !== 'marco_prazo' || (Boolean(l.marco) && l.dias !== null && Boolean(l.tipoDias))) && (l.tipo !== 'dias_apos_pagamento' || (l.dias !== null && Boolean(l.tipoDias)));
export const repasseCompleto = (r: Pick<CampoRepasse, 'marco' | 'dias' | 'tipoDias'>) => Boolean(r.marco) && r.dias !== null && Boolean(r.tipoDias);
const reconhecimentoDefinido = (l: Pick<CampoLiberacao, 'reconhecimentoDias' | 'reconhecimentoTipoDias'>) => l.reconhecimentoDias !== null && l.reconhecimentoDias !== undefined && Boolean(l.reconhecimentoTipoDias);

export function textoLiberacao(l: Pick<CampoLiberacao, 'tipo' | 'marco' | 'dias' | 'tipoDias'> & Partial<Pick<CampoLiberacao, 'reconhecimentoDias' | 'reconhecimentoTipoDias'>>): string {
  if (!l.tipo) return A_CONFIRMAR;
  if (l.tipo === 'marco_prazo' && !l.marco && l.dias === null) return 'Marco + prazo (marco e prazo a definir)';
  if (l.tipo === 'marco_prazo') return `${rotuloDe(MARCOS_LIBERACAO, l.marco)} + ${diasTexto(l.dias, l.tipoDias)}`;
  if (l.tipo === 'dias_apos_pagamento') return `Pagamento do comprador ao Cliente + ${diasTexto(l.dias, l.tipoDias)}`;
  const rec = l.reconhecimentoDias !== null && l.reconhecimentoDias !== undefined ? ` · reconhecimento do pagamento: ${diasTexto(l.reconhecimentoDias, l.reconhecimentoTipoDias ?? null)}` : ' · reconhecimento do pagamento: a confirmar';
  return `${rotuloDe(TIPOS_LIBERACAO, l.tipo)}${rec}`;
}
export function textoRepasse(r: Pick<CampoRepasse, 'marco' | 'dias' | 'tipoDias'> & Partial<Pick<CampoRepasse, 'descricao'>>): string {
  if (!r.marco && r.dias === null) return A_CONFIRMAR;
  const marco = r.marco === 'outro' ? `Outro: ${r.descricao || A_CONFIRMAR}` : rotuloDe(MARCOS_REPASSE, r.marco);
  return `${marco} + ${diasTexto(r.dias, r.tipoDias)}`;
}

// ---------------------------------------------------------------- dinheiro (centavos) e distribuição

export const centavos = (v: number | string) => Math.round(Number(v) * 100);
export const reais = (c: number) => (c / 100).toFixed(2);

/**
 * Distribuição proporcional às bases (parcelas do Cliente), em centavos: cada item = total × base ÷ soma das bases,
 * arredondado; o resíduo de arredondamento fica no ÚLTIMO item e a soma é exata. Bases zeradas: tudo no último.
 */
export function distribuirProporcional(totalCent: number, basesCent: number[]): number[] {
  if (!basesCent.length) return [];
  const soma = basesCent.reduce((s, b) => s + b, 0);
  if (soma <= 0) return basesCent.map((_, i) => (i === basesCent.length - 1 ? totalCent : 0));
  const out = basesCent.map((b) => Math.round((totalCent * b) / soma));
  out[out.length - 1] = totalCent - out.slice(0, -1).reduce((s, x) => s + x, 0);
  return out;
}

// ---------------------------------------------------------------- datas

const RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const utc = (s: string) => {
  const m = RE.exec(s);
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : NaN;
};
const iso = (t: number) => new Date(t).toISOString().slice(0, 10);
export const ehData = (s: unknown): s is string => typeof s === 'string' && RE.test(s) && !Number.isNaN(utc(s));
export const somarCorridos = (s: string, n: number) => iso(utc(s) + n * 86_400_000);
const fimDeSemana = (t: number) => [0, 6].includes(new Date(t).getUTCDay());
/** Dias úteis = segunda a sexta. Feriados não são conhecidos pelo HUB (a tela avisa). */
export function somarUteis(s: string, n: number) {
  let t = utc(s);
  let falta = n;
  while (falta > 0) {
    t += 86_400_000;
    if (!fimDeSemana(t)) falta--;
  }
  return iso(t);
}
export const somarDias = (s: string, n: number, t: TipoDias) => (t === 'uteis' ? somarUteis(s, n) : somarCorridos(s, n));
export const diasEntre = (de: string, ate: string) => Math.round((utc(ate) - utc(de)) / 86_400_000);
export const dataBr = (s: string | null | undefined) => (s && RE.test(s) ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '—');
/**
 * Calendário do Cliente: prazo em dias CORRIDOS da NF × vencimento. O prazo é opcional (sem ele vale só o vencimento);
 * quando vem junto, os dois precisam bater (vencimento = NF + prazo), sem ajuste de fim de semana. Devolve o motivo
 * da recusa, ou null quando está coerente.
 */
export function conferirPrazoVencimento(dataNf: string | null, prazoDias: number | null, vencimento: string): string | null {
  if (prazoDias === null) return null;
  if (!dataNf) return `prazo de ${prazoDias} dias sem data da NF para contar: informe só o vencimento`;
  const esperado = somarCorridos(dataNf, prazoDias);
  return esperado === vencimento ? null : `${prazoDias} dias da NF (${dataBr(dataNf)}) dão ${dataBr(esperado)}, mas o vencimento informado é ${dataBr(vencimento)}: corrija o prazo ou o vencimento`;
}
/** Soma meses mantendo o dia; quando o dia não existe no mês de destino, usa o último dia dele (31/01 + 1 → 28/02). */
export function somarMeses(s: string, n: number) {
  const [a, m, d] = s.split('-').map(Number);
  const alvo = new Date(Date.UTC(a, m - 1 + n, 1));
  const ultimo = new Date(Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth() + 1, 0)).getUTCDate();
  return iso(Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth(), Math.min(d, ultimo)));
}

// ---------------------------------------------------------------- calendário do Cliente e redistribuição (Pacote 5)

/** De onde vem o calendário do Cliente informado no Financeiro (obrigatório; o texto "6x" sozinho não basta). */
export const ORIGENS_CALENDARIO = [
  { id: 'demonstrativo', nome: 'Demonstrativo do parceiro (adquirente)' },
  { id: 'comprovante_cliente', nome: 'Comprovante do Cliente' },
  { id: 'condicao_pedido', nome: 'Condição estruturada do Pedido' },
  // E0: a versão do cadastro de condições EM VIGOR na data do envio da Proposta (fonte histórica) e, só como
  // alternativa identificada, o cadastro atual conferido pelo Financeiro. O texto livre nunca vira calendário.
  { id: 'condicao_envio', nome: 'Condição vigente no envio da Proposta' },
  { id: 'cadastro_atual', nome: 'Cadastro atual de condições, conferido pelo Financeiro' },
] as const;
export type OrigemCalendario = (typeof ORIGENS_CALENDARIO)[number]['id'];
/** De onde vem o VALOR de cada parcela do calendário: documento (boleto, OC, demonstrativo) ou divisão igual sugerida. */
export const ORIGENS_VALOR_CALENDARIO = [
  { id: 'documentado', nome: 'Documentado' },
  { id: 'sugestao_igual', nome: 'Divisão igual · sugestão' },
] as const;
export type OrigemValorCalendario = (typeof ORIGENS_VALOR_CALENDARIO)[number]['id'];
/** Origem da informação de uma redistribuição. */
export const ORIGENS_REDISTRIBUICAO = [
  { id: 'informado_parceiro', nome: 'Informado pelo parceiro' },
  { id: 'sistema_distribuidor', nome: 'Sistema do distribuidor' },
  { id: 'informacao_solicitada', nome: 'Informação solicitada ao parceiro' },
  { id: 'demonstrativo', nome: 'Demonstrativo do parceiro' },
  { id: 'comprovante_cliente', nome: 'Comprovante do Cliente' },
  { id: 'outra', nome: 'Outra' },
] as const;
export const ROTULO_ORIGEM_DISTRIBUICAO: Record<string, string> = Object.fromEntries([...ORIGENS_REDISTRIBUICAO, ...ORIGENS_CALENDARIO].map((o) => [o.id, o.nome]));
export const PERIODICIDADES_CALENDARIO = [
  { id: 'mensal', nome: 'Mensal' },
  { id: 'dias', nome: 'A cada N dias' },
] as const;
export type PeriodicidadeCalendario = (typeof PERIODICIDADES_CALENDARIO)[number]['id'];
/**
 * Tratamento do prazo negociado de uma parcela redistribuída — escolha EXPLÍCITA na janela (sem escolha, nada é gravado).
 * O prazo negociado nunca passa sozinho para outra parcela; o histórico é preservado em qualquer caso.
 */
export const TRATAMENTOS_PRAZO = [
  { id: 'manter', nome: 'Manter o prazo negociado nesta parcela' },
  { id: 'retorno_calculo', nome: 'Voltar à data calculada pelo novo vencimento' },
  { id: 'nova_data', nome: 'Informar novo prazo negociado' },
] as const;
export type TratamentoPrazo = (typeof TRATAMENTOS_PRAZO)[number]['id'];

/** Calendário do Cliente: n parcelas de base igual (resíduo em centavos na última) a partir do 1º vencimento. */
export function calendarioCliente(totalCent: number, quantidade: number, primeiro: string, periodicidade: PeriodicidadeCalendario, intervaloDias = 30) {
  const parte = Math.floor(totalCent / quantidade);
  return Array.from({ length: quantidade }, (_, i) => ({
    baseCent: i === quantidade - 1 ? totalCent - parte * (quantidade - 1) : parte,
    vencimento: periodicidade === 'mensal' ? somarMeses(primeiro, i) : somarCorridos(primeiro, i * intervaloDias),
  }));
}

/**
 * Calendário pela LISTA EXATA de prazos (dias corridos da NF do parceiro), como 30/45/90: os prazos nunca viram
 * "1º vencimento + periodicidade". Os valores são a divisão igual (resíduo na última) — uma SUGESTÃO, marcada como tal,
 * para quando a fonte não traz percentuais ou valores; valores documentados entram como estão, na janela.
 */
export function parcelasPorPrazos(totalCent: number, dataNf: string, dias: number[]) {
  const parte = Math.floor(totalCent / dias.length);
  return dias.map((d, i) => ({ prazoDias: d, vencimento: somarCorridos(dataNf, d), baseCent: i === dias.length - 1 ? totalCent - parte * (dias.length - 1) : parte }));
}

// ---------------------------------------------------------------- liberação e repasse

/** Pagamento do Cliente ao parceiro (ou do comprador ao Cliente), ativo (não estornado). Valores em centavos. */
export interface FatoPagamento {
  parcela: string;
  data: string;
  valor: number;
  registradoEm: string;
}
export interface ItemParaLiberar {
  parcela: string;
  baseCent: number | null;
  vencimentoParcela: string | null;
}
export type SituacaoLiberacao = 'a_confirmar' | 'aguardando_cliente' | 'aguardando_liberacao' | 'liberado';
export interface Liberacao {
  situacao: SituacaoLiberacao;
  /** data EFETIVA da liberação (ou a prevista, quando "aguardando liberação" com marco conhecido) */
  data: string | null;
  /** fato que libera (texto) e a data em que esse fato foi REGISTRADO no HUB */
  fato: string;
  registradoEm: string | null;
  /** data efetiva do pagamento que liberou (marco "pagamento do Cliente" do repasse) */
  dataPagamento: string | null;
  /** pagamento + prazo de reconhecimento pelo parceiro (marco "reconhecimento" do repasse); nula sem o prazo */
  dataReconhecimento: string | null;
  atrasoCliente: number | null;
  pagoCent: number;
}

function pagamentoQueCompleta(pags: FatoPagamento[], parcela: string, baseCent: number | null) {
  const meus = pags.filter((p) => p.parcela === parcela).sort((a, b) => (a.data === b.data ? a.registradoEm.localeCompare(b.registradoEm) : a.data.localeCompare(b.data)));
  let soma = 0;
  for (const p of meus) {
    soma += p.valor;
    if (baseCent === null || soma >= baseCent) return { pagamento: p, pago: meus.reduce((s, x) => s + x.valor, 0) };
  }
  return { pagamento: null, pago: soma };
}

type CondLiberacao = Pick<CampoLiberacao, 'tipo' | 'marco' | 'dias' | 'tipoDias'> & Partial<Pick<CampoLiberacao, 'reconhecimentoDias' | 'reconhecimentoTipoDias'>>;
export interface ContextoLiberacao {
  pagamentos: FatoPagamento[];
  primeira: ItemParaLiberar;
  marcos: Partial<Record<MarcoLiberacao, string>>;
  hoje: string;
  /** reconhecimento EFETIVO do pagamento pelo parceiro, registrado por parcela do Cliente (data) */
  reconhecimentos?: Record<string, string>;
}

/**
 * Liberação de um item pela condição EM VIGOR para ele, só por FATOS com data efetiva: informar hoje um pagamento antigo
 * libera na data do pagamento (não na do registro). Liberações por pagamento: libera com a parcela QUITADA (pagamento
 * parcial não libera). O prazo de reconhecimento do pagamento só PROJETA a data (projetarItem): o decurso dos dias não
 * é confirmação do parceiro; o reconhecimento efetivo é um fato registrado (dataReconhecimento). Sem condição: "A
 * confirmar" (nada é liberado).
 */
export function liberarItem(item: ItemParaLiberar, cond: CondLiberacao, ctx: ContextoLiberacao): Liberacao {
  const base = { data: null, fato: '', registradoEm: null, dataPagamento: null, dataReconhecimento: null, atrasoCliente: null, pagoCent: 0 };
  if (!cond.tipo) return { ...base, situacao: 'a_confirmar', fato: 'condição de liberação não definida' };
  const atraso = (venc: string | null) => (venc && venc < ctx.hoje ? diasEntre(venc, ctx.hoje) : null);
  if (cond.tipo === 'proporcional' || cond.tipo === 'mensalidade' || cond.tipo === 'integral_primeira') {
    const alvo = cond.tipo === 'integral_primeira' ? ctx.primeira : item;
    const { pagamento, pago } = pagamentoQueCompleta(ctx.pagamentos, alvo.parcela, alvo.baseCent);
    const nome = cond.tipo === 'integral_primeira' ? 'parcela 1' : 'parcela';
    if (!pagamento) return { ...base, situacao: 'aguardando_cliente', pagoCent: pago, fato: pago > 0 ? `pagamento parcial da ${nome} (nada é liberado até a quitação)` : `aguardando o pagamento da ${nome}`, atrasoCliente: atraso(alvo.vencimentoParcela) };
    const rec = ctx.reconhecimentos?.[alvo.parcela] ?? null;
    return { ...base, situacao: 'liberado', pagoCent: pago, data: pagamento.data, dataPagamento: pagamento.data, dataReconhecimento: rec, registradoEm: pagamento.registradoEm, fato: `Cliente pagou a ${nome} em ${dataBr(pagamento.data)}${rec ? `; reconhecido pelo parceiro em ${dataBr(rec)}` : ''}` };
  }
  if (cond.tipo === 'marco_prazo') {
    if (!cond.marco || cond.dias === null || !cond.tipoDias) return { ...base, situacao: 'a_confirmar', fato: 'marco ou prazo da liberação a confirmar' };
    const marco = ctx.marcos[cond.marco];
    if (!marco) return { ...base, situacao: 'aguardando_liberacao', fato: `aguardando o marco: ${rotuloDe(MARCOS_LIBERACAO, cond.marco).toLowerCase()}` };
    const data = somarDias(marco, cond.dias, cond.tipoDias);
    return { ...base, situacao: data <= ctx.hoje ? 'liberado' : 'aguardando_liberacao', data, fato: `marco em ${dataBr(marco)} + ${diasTexto(cond.dias, cond.tipoDias)}` };
  }
  // dias após o pagamento do comprador ao Cliente (Trade-in)
  if (cond.dias === null || !cond.tipoDias) return { ...base, situacao: 'a_confirmar', fato: 'prazo após o pagamento ao Cliente a confirmar' };
  const { pagamento } = pagamentoQueCompleta(ctx.pagamentos, item.parcela, null);
  if (!pagamento) return { ...base, situacao: 'aguardando_cliente', fato: 'aguardando o pagamento do comprador ao Cliente' };
  const data = somarDias(pagamento.data, cond.dias, cond.tipoDias);
  return { ...base, situacao: data <= ctx.hoje ? 'liberado' : 'aguardando_liberacao', data, dataPagamento: pagamento.data, registradoEm: pagamento.registradoEm, fato: `pago ao Cliente em ${dataBr(pagamento.data)} + ${diasTexto(cond.dias, cond.tipoDias)}` };
}

export type SituacaoRepasse = 'a_confirmar' | 'aguardando_marco' | 'previsto' | 'vencido';
export interface Repasse {
  situacao: SituacaoRepasse;
  vencimento: string | null;
  marcoData: string | null;
  detalhe: string;
}
/**
 * Fatos do lado da Inttrix que podem ser marco do repasse, cada um com a SUA data efetiva: a solicitação do resgate
 * (RSG) e a emissão da nota da Inttrix (NFI) são fatos distintos.
 */
export interface FatosRepasse {
  resgate?: string | null;
  notaInttrix?: string | null;
}
/** Data EFETIVA do marco do repasse, se já ocorreu (nunca estimada). */
function marcoEfetivo(r: Pick<CampoRepasse, 'marco'>, lib: Liberacao, fatos: FatosRepasse): string | null {
  switch (r.marco) {
    case 'pagamento_cliente':
      return lib.dataPagamento;
    case 'reconhecimento':
      return lib.dataReconhecimento;
    case 'liberacao':
      return lib.situacao === 'liberado' ? lib.data : null;
    case 'solicitacao_resgate':
      return fatos.resgate ?? null;
    case 'nf_inttrix':
      return fatos.notaInttrix ?? null;
    default:
      return null;
  }
}
/**
 * Vencimento do repasse pelo MARCO e PRAZO acordados, só com o marco EFETIVO. Marco ou prazo desconhecido: "A
 * confirmar", sem vencimento inventado. Marco ainda não ocorrido: aguardando o marco (a PREVISÃO estimada fica em
 * projetarItem). "Vencido" quer dizer só que a data passou: a causa é apurada na previsão.
 */
export function vencimentoRepasse(r: Pick<CampoRepasse, 'marco' | 'dias' | 'tipoDias'> & Partial<Pick<CampoRepasse, 'descricao'>>, lib: Liberacao, hoje: string, fatos: FatosRepasse = {}): Repasse {
  if (!repasseCompleto(r)) return { situacao: 'a_confirmar', vencimento: null, marcoData: null, detalhe: 'marco ou prazo do repasse a confirmar (sem vencimento)' };
  const marcoData = marcoEfetivo(r, lib, fatos);
  const nomeMarco = r.marco === 'outro' ? `“${r.descricao || 'outro marco'}”` : rotuloDe(MARCOS_REPASSE, r.marco).toLowerCase();
  if (!marcoData) return { situacao: 'aguardando_marco', vencimento: null, marcoData: null, detalhe: `aguardando o marco: ${nomeMarco} + ${diasTexto(r.dias, r.tipoDias)}` };
  const vencimento = somarDias(marcoData, r.dias!, r.tipoDias!);
  return { situacao: vencimento < hoje ? 'vencido' : 'previsto', vencimento, marcoData, detalhe: `${nomeMarco} em ${dataBr(marcoData)} + ${diasTexto(r.dias, r.tipoDias)}` };
}

// ---------------------------------------------------------------- previsão (prevista × real)

export interface DataPrevista {
  data: string | null;
  /** true = projeção (o fato ainda não ocorreu); false = data efetiva */
  estimada: boolean;
}
export interface Projecao {
  /** vencimento previsto da parcela do Cliente (pela NF do parceiro + condição de pagamento) */
  vencimentoCliente: string | null;
  /** pagamento do Cliente ao parceiro: efetivo (quitado), parcial ou previsto pelo vencimento */
  pagamentoCliente: DataPrevista & { parcial: boolean };
  liberacao: DataPrevista;
  /** reconhecimento do pagamento pelo parceiro: registrado (efetivo) ou projetado pelo prazo configurado */
  reconhecimento: DataPrevista | null;
  /** solicitação do resgate pela Inttrix (RSG): efetiva ou projetada na liberação */
  resgate: DataPrevista | null;
  /** data esperada de recebimento pela Inttrix CALCULADA (efetiva quando já recebida) */
  recebimento: DataPrevista & { efetivo: boolean };
  /** como a data esperada foi calculada (para o histórico e a tela) */
  fundamento: string;
  /** o que falta para haver previsão (vazio quando há data) */
  falta: string[];
  /** previsão vencida sem recebimento: a causa provável (nunca presume atraso do parceiro) */
  vencida: string | null;
}

/**
 * PREVISÃO de um item desde o faturamento do parceiro, sem esperar pagamento, confirmação ou liberação:
 * vencimento previsto da parcela do Cliente → pagamento (efetivo quando quitado) → reconhecimento do pagamento pelo
 * parceiro (registrado, ou projetado pelo prazo) → solicitação do resgate / nota da Inttrix (efetivas, ou projetadas) →
 * data esperada de recebimento (marco do repasse + prazo). Cada etapa usa o FATO quando ele existe e a projeção
 * (identificada) quando não; sem dado suficiente, diz exatamente o que falta. Nada é inventado.
 */
export function projetarItem(
  item: ItemParaLiberar,
  cond: CondicaoOperacao,
  ctx: ContextoLiberacao & { resgate?: string | null; notaInttrix?: string | null; recebimentoEfetivo?: string | null },
): Projecao {
  const lib = liberarItem(item, cond.liberacao, ctx);
  const l = cond.liberacao;
  const r = cond.repasse;
  const falta: string[] = [];
  const passos: string[] = [];
  const porPagamento = Boolean(l.tipo) && LIBERACOES_POR_PAGAMENTO.includes(l.tipo!);
  const alvo = l.tipo === 'integral_primeira' ? ctx.primeira : item;
  const vencimentoCliente = alvo.vencimentoParcela;
  let pagamento: DataPrevista & { parcial: boolean } = { data: null, estimada: true, parcial: lib.pagoCent > 0 && !lib.dataPagamento };
  let liberacao: DataPrevista = { data: null, estimada: true };
  let reconhecimento: DataPrevista | null = null;

  if (!l.tipo) falta.push('condição de liberação');
  else if (porPagamento) {
    if (lib.dataPagamento) {
      pagamento = { data: lib.dataPagamento, estimada: false, parcial: false };
      passos.push(`pago pelo Cliente em ${dataBr(lib.dataPagamento)}`);
    } else if (vencimentoCliente) {
      pagamento = { data: vencimentoCliente, estimada: true, parcial: pagamento.parcial };
      passos.push(`vencimento do Cliente ${dataBr(vencimentoCliente)}${pagamento.parcial ? ' (pagamento parcial: não libera até a quitação)' : ''}`);
    } else falta.push('vencimento da parcela do Cliente (condição de pagamento não estruturada)');
    // liberação: o pagamento quitado (fato); antes dele, o vencimento (projeção)
    if (pagamento.data) liberacao = { data: pagamento.data, estimada: pagamento.estimada };
    // reconhecimento: o fato registrado; sem ele, pagamento + prazo configurado (projeção, não confirmação)
    if (lib.dataReconhecimento) reconhecimento = { data: lib.dataReconhecimento, estimada: false };
    else if (pagamento.data && reconhecimentoDefinido(l)) reconhecimento = { data: somarDias(pagamento.data, l.reconhecimentoDias!, l.reconhecimentoTipoDias!), estimada: true };
  } else if (l.tipo === 'marco_prazo') {
    if (lib.data) {
      liberacao = { data: lib.data, estimada: lib.situacao !== 'liberado' };
      passos.push(lib.fato);
    } else falta.push(l.marco ? `data do marco: ${rotuloDe(MARCOS_LIBERACAO, l.marco).toLowerCase()}` : 'marco e prazo da liberação');
  } else {
    if (lib.data) {
      pagamento = { data: lib.dataPagamento, estimada: false, parcial: false };
      liberacao = { data: lib.data, estimada: lib.situacao !== 'liberado' };
      passos.push(lib.fato);
    } else falta.push(l.dias === null || !l.tipoDias ? 'prazo após o pagamento ao Cliente' : 'data do pagamento do comprador ao Cliente');
  }
  // solicitação do resgate: o fato (RSG); sem ele, projetada na liberação
  const resgate: DataPrevista | null = ctx.resgate ? { data: ctx.resgate, estimada: false } : liberacao.data ? { data: liberacao.data, estimada: true } : null;

  let recebimento: Projecao['recebimento'] = { data: null, estimada: true, efetivo: false };
  if (ctx.recebimentoEfetivo) {
    recebimento = { data: ctx.recebimentoEfetivo, estimada: false, efetivo: true };
    passos.push(`recebido em ${dataBr(ctx.recebimentoEfetivo)}`);
  } else if (!repasseCompleto(r)) falta.push('prazo de repasse (marco, dias e tipo de dias)');
  else {
    let marco: DataPrevista | null = null;
    if (r.marco === 'pagamento_cliente') marco = pagamento.data ? pagamento : null;
    else if (r.marco === 'reconhecimento') {
      marco = reconhecimento;
      if (!marco) falta.push(porPagamento && pagamento.data ? 'prazo de reconhecimento do pagamento (dias e tipo) ou o reconhecimento registrado' : 'data do pagamento do Cliente');
      else passos.push(marco.estimada ? `+ ${diasTexto(l.reconhecimentoDias!, l.reconhecimentoTipoDias!)} de reconhecimento (projetado)` : `reconhecido pelo parceiro em ${dataBr(marco.data)}`);
    } else if (r.marco === 'liberacao') marco = liberacao.data ? liberacao : null;
    else if (r.marco === 'solicitacao_resgate') {
      marco = resgate;
      if (marco) passos.push(marco.estimada ? 'solicitação de resgate projetada na liberação' : `resgate solicitado em ${dataBr(marco.data)}`);
    } else if (r.marco === 'nf_inttrix') {
      marco = ctx.notaInttrix ? { data: ctx.notaInttrix, estimada: false } : resgate ? { data: resgate.data, estimada: true } : null;
      if (marco) passos.push(marco.estimada ? `nota da Inttrix projetada ${resgate?.estimada ? 'na liberação' : 'no resgate solicitado'}` : `nota da Inttrix emitida em ${dataBr(marco.data)}`);
    } else falta.push(`data do marco do repasse: “${r.descricao || 'outro'}”`);
    if (marco?.data) {
      recebimento = { data: somarDias(marco.data, r.dias!, r.tipoDias!), estimada: true, efetivo: false };
      passos.push(`+ ${diasTexto(r.dias, r.tipoDias)} de repasse (a partir de: ${r.marco === 'outro' ? r.descricao : rotuloDe(MARCOS_REPASSE, r.marco).toLowerCase()})`);
    } else if (r.marco !== 'outro' && !falta.length) falta.push(`data do marco do repasse: ${rotuloDe(MARCOS_REPASSE, r.marco).toLowerCase()}`);
  }

  let vencida: string | null = null;
  if (!recebimento.efetivo && recebimento.data && recebimento.data < ctx.hoje) {
    if (porPagamento && !lib.dataPagamento) vencida = vencimentoCliente && vencimentoCliente < ctx.hoje ? 'pagamento do Cliente não registrado (possível atraso do Cliente)' : 'aguardando o pagamento do Cliente';
    else if (lib.situacao !== 'liberado') vencida = 'aguardando a liberação';
    else if (r.marco === 'reconhecimento' && !lib.dataReconhecimento) vencida = 'aguardando o reconhecimento do pagamento pelo parceiro';
    else if (r.marco === 'solicitacao_resgate' && !ctx.resgate) vencida = 'aguardando a solicitação de resgate';
    else if (r.marco === 'nf_inttrix' && !ctx.notaInttrix) vencida = 'aguardando a nota da Inttrix';
    else vencida = 'repasse do parceiro vencido';
  }
  return {
    vencimentoCliente,
    pagamentoCliente: pagamento,
    liberacao,
    reconhecimento,
    resgate: r.marco === 'solicitacao_resgate' || r.marco === 'nf_inttrix' || ctx.resgate ? resgate : null,
    recebimento,
    fundamento: recebimento.data ? passos.join(' ') : '',
    falta: recebimento.data ? [] : [...new Set(falta)],
    vencida,
  };
}

export const ROTULO_LIBERACAO: Record<SituacaoLiberacao, string> = {
  a_confirmar: 'Liberação a confirmar',
  aguardando_cliente: 'Aguardando Cliente',
  aguardando_liberacao: 'Aguardando liberação',
  liberado: 'Liberado',
};
/** Rótulo de disponibilidade para nota: só liberado E com valor confirmado (a estimativa nunca vai para nota). */
export function disponibilidade(lib: SituacaoLiberacao, confirmado: boolean): 'liberado_aguardando_confirmacao' | 'disponivel_nota' | null {
  if (lib !== 'liberado') return null;
  return confirmado ? 'disponivel_nota' : 'liberado_aguardando_confirmacao';
}
export const ROTULO_DISPONIBILIDADE = { liberado_aguardando_confirmacao: 'Liberado · aguardando confirmação do valor', disponivel_nota: 'Disponível para resgate' } as const;

/**
 * Saldo disponível para nota (base da E2): só itens LIBERADOS e com valor CONFIRMADO, pelo confirmado, menos o que já
 * foi cobrado em notas ativas. Se o confirmado subir depois de uma nota, o saldo é só a DIFERENÇA (nota complementar
 * vinculada à original, sem duplicar o valor original). Na E1 ainda não há notas: cobradoCent = 0.
 */
export function saldoParaNota(item: { liberado: boolean; confirmadoCent: number | null; cobradoCent: number }): number {
  if (!item.liberado || item.confirmadoCent === null) return 0;
  return Math.max(0, item.confirmadoCent - item.cobradoCent);
}
